import { useState } from "react";
import { trackRumAction } from "@/lib/datadog";
import {
  Bell,
  Trash2,
  Pencil,
  Loader2,
  Mail,
  Smartphone,
  Plus,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NotificationConfigForm } from "@/components/notifications";
import {
  useNotificationConfigs,
  useDeleteNotificationConfig,
} from "@/hooks/api/useNotifications";
import type { NotificationConfig } from "@/api/types/notifications";
import { toast } from "sonner";

export default function NotificationsPage() {
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToEdit, setConfigToEdit] = useState<NotificationConfig | null>(
    null
  );
  const [configToDelete, setConfigToDelete] =
    useState<NotificationConfig | null>(null);

  // Fetch configs
  const {
    data: configsData,
    loading: configsLoading,
    refetch: refetchConfigs,
  } = useNotificationConfigs({ is_active: true });

  // Delete mutation
  const { execute: deleteConfig, loading: deleting } =
    useDeleteNotificationConfig();

  // API returns array directly (not { items: [...] })
  const configs = configsData ?? [];

  const handleCreateClick = () => {
    trackRumAction('notification_config_action', { action: 'create' });
    setConfigToEdit(null); // Clear any previous edit config
    setFormDialogOpen(true);
  };

  const handleEditClick = (config: NotificationConfig) => {
    trackRumAction('notification_config_action', { action: 'edit' });
    setConfigToEdit(config);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (config: NotificationConfig) => {
    trackRumAction('notification_config_action', { action: 'delete' });
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleFormSuccess = () => {
    setFormDialogOpen(false);
    setConfigToEdit(null);
    refetchConfigs();
  };

  const handleFormCancel = () => {
    setFormDialogOpen(false);
    setConfigToEdit(null);
  };

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return;

    // deleteConfig returns undefined on success (204 No Content), null on error
    const result = await deleteConfig(configToDelete.id);
    if (result === null) {
      toast.error("Failed to delete configuration");
    } else {
      toast.success("Alert configuration deleted");
      refetchConfigs();
    }
    setDeleteDialogOpen(false);
    setConfigToDelete(null);
  };

  // Format variable slug to readable name (e.g., "estimate5th" → "Estimate 5th")
  const formatVariableName = (name: string | undefined, slug: string) => {
    // If we have a proper name, use it
    if (name && name !== slug) return name;

    // Otherwise format the slug
    return (
      slug
        // Insert space before numbers
        .replace(/(\d+)/g, " $1")
        // Insert space before capital letters
        .replace(/([A-Z])/g, " $1")
        // Replace underscores with spaces
        .replace(/_/g, " ")
        // Capitalize first letter of each word
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ")
        .trim()
    );
  };

  const formatChannels = (channels: string[]) => {
    return (
      <TooltipProvider>
        <div className="flex gap-1.5">
          {channels.map((channel) => {
            if (channel === "email") {
              return (
                <Tooltip key={channel}>
                  <TooltipTrigger asChild>
                    <div className="p-1.5 rounded-md hover:bg-muted/20 cursor-default">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Email</TooltipContent>
                </Tooltip>
              );
            }
            if (channel === "in_app") {
              return (
                <Tooltip key={channel}>
                  <TooltipTrigger asChild>
                    <div className="p-1.5 rounded-md hover:bg-muted/20 cursor-default">
                      <Smartphone className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>In-App</TooltipContent>
                </Tooltip>
              );
            }
            return null;
          })}
        </div>
      </TooltipProvider>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                <Bell className="h-7 w-7 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  Alert Settings
                </h1>
                <p className="text-muted-foreground">
                  Configure alerts for storm impact forecasts
                </p>
              </div>
            </div>
            <Button onClick={handleCreateClick} size="lg">
              <Plus className="h-5 w-5 mr-1" />
              Create New Alert
            </Button>
          </div>
        </div>

        {/* Active Alerts Table */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">Active Alerts</CardTitle>
            <CardDescription>
              Your configured alert rules ({configs.length} total)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {configsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : configs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Bell className="h-16 w-16 text-muted-foreground/30 mb-4" />
                <p className="text-lg text-muted-foreground mb-2">
                  No alert configurations yet
                </p>
                <p className="text-sm text-muted-foreground/70 mb-6">
                  Create your first alert to start monitoring storm impacts
                </p>
                <Button onClick={handleCreateClick}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Alert
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold">Location</TableHead>
                    <TableHead className="font-semibold">Storm Model</TableHead>
                    <TableHead className="font-semibold">Weather Model</TableHead>
                    <TableHead className="font-semibold">Variable</TableHead>
                    <TableHead className="font-semibold">Threshold</TableHead>
                    <TableHead className="font-semibold">Channels</TableHead>
                    <TableHead className="w-[80px] font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {configs.map((config) => (
                    <TableRow key={config.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {config.geographic_node_name || "Unknown Location"}
                          </p>
                          {config.geographic_node_resolution && (
                            <p className="text-xs text-muted-foreground">
                              {config.geographic_node_resolution}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {config.storm_impact_model_name || "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {config.weather_model_name || "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatVariableName(
                          config.threshold_variable_name,
                          config.threshold_variable_slug
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium">
                          {config.comparison_operator || ">"}{" "}
                          {config.threshold_value.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>{formatChannels(config.channels)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:bg-muted/20 hover:text-primary"
                            onClick={() => handleEditClick(config)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:bg-muted/20 hover:text-destructive"
                            onClick={() => handleDeleteClick(config)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={formDialogOpen} onOpenChange={setFormDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {configToEdit ? "Edit Alert Configuration" : "Create New Alert"}
            </DialogTitle>
            <DialogDescription>
              {configToEdit
                ? "Update the settings for this alert configuration"
                : "Set up a new alert to monitor storm impacts"}
            </DialogDescription>
          </DialogHeader>
          <NotificationConfigForm
            key={configToEdit?.id || "create"} // Force re-mount when switching between create/edit
            config={configToEdit || undefined}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Alert Configuration</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this alert configuration? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {configToDelete && (
            <div className="bg-muted/50 rounded-md p-4 my-4">
              <p className="font-medium">
                {configToDelete.geographic_node_name || "Unknown Location"}
              </p>
              <p className="text-sm text-muted-foreground">
                {configToDelete.threshold_variable_name ||
                  configToDelete.threshold_variable_slug}{" "}
                <span className="font-mono">
                  {configToDelete.comparison_operator || ">"}
                </span>{" "}
                {configToDelete.threshold_value.toLocaleString()}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
